import { Navigation } from "lucide-react";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DIRECTIONS_UNAVAILABLE, destinationFor, directionsUrl } from "@/lib/directions";

/** Corner overlay that opens Google Maps directions to THIS property.
 *
 *  The URL is built inside the click handler from the `property` prop currently rendered, so the
 *  destination can never belong to a property the user has navigated away from. On a phone the
 *  standard `?api=1` link is handed to the Google Maps app when it is installed and falls back to
 *  Maps in the browser when it is not; on desktop it opens in a new tab.
 */
export function DirectionsButton({ property, size = "md", className = "" }) {
  const url = directionsUrl(property);
  const destination = destinationFor(property);
  const disabled = !url;
  const box = size === "sm" ? "h-10 w-10" : "h-11 w-11";
  const icon = size === "sm" ? "h-[17px] w-[17px]" : "h-[18px] w-[18px]";

  const open = (e) => {
    // These buttons sit inside property cards that are themselves links.
    e.preventDefault();
    e.stopPropagation();
    const fresh = directionsUrl(property);
    if (!fresh) {
      toast.error(DIRECTIONS_UNAVAILABLE);
      return;
    }
    window.open(fresh, "_blank", "noopener,noreferrer");
  };

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={open}
            aria-label={disabled ? DIRECTIONS_UNAVAILABLE : `Get directions to ${property?.name || "this property"}`}
            aria-disabled={disabled}
            data-testid={`directions-btn-${property?._id || "unknown"}`}
            data-destination={destination || ""}
            data-directions-url={url || ""}
            data-property-id={property?._id || ""}
            className={`${box} ${className} inline-flex items-center justify-center rounded-full
              border backdrop-blur-md shadow-lg transition-all duration-200 outline-none
              focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2
              focus-visible:ring-offset-royal-deep
              ${disabled
                ? "cursor-not-allowed border-white/30 bg-royal-deep/55 text-white/65"
                : "border-gold/40 bg-royal-deep/70 text-gold hover:bg-gold hover:text-royal-deep hover:border-gold hover:scale-105 active:scale-95 active:bg-gold/90"}`}
          >
            <Navigation className={`${icon} ${disabled ? "" : "drop-shadow"}`} strokeWidth={2.25} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-[240px] text-xs">
          {disabled ? DIRECTIONS_UNAVAILABLE : "Get Directions"}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default DirectionsButton;
